import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { outreach, outreachQueries } from '@round/api';
import {
    useAddOutreachResultMutation,
    useArchiveOutreachResultMutation,
    useUnarchiveOutreachResultMutation,
} from '@round/creatorbase-helpers/api-hooks';
import { usePatchOutreachResultsCache } from './usePatchOutreachResultsCache';

export type BulkAction = 'add' | 'archive' | 'unarchive';

const errorUtils = {
    add: outreachQueries.outreachRequestResults.utils.add,
    archive: outreachQueries.outreachRequestResults.utils.archive,
    unarchive: outreachQueries.outreachRequestResults.utils.unarchive,
};

export type BulkActionResult = {
    succeededCount: number;
    failedCount: number;
    /** What the endpoint said about the first row it refused; null when it refused none. */
    errorMessage: string | null;
};

const isFulfilled = <T>(outcome: PromiseSettledResult<T>): outcome is PromiseFulfilledResult<T> =>
    outcome.status === 'fulfilled';

const isRejected = <T>(outcome: PromiseSettledResult<T>): outcome is PromiseRejectedResult =>
    outcome.status === 'rejected';

/**
 * Runs one of the result actions over a set of rows. The endpoint takes a result at a time, so a
 * batch is that call made once per row: the rows are independent, and one refused leaves the rest.
 */
export function useOutreachResultsBulkActions() {
    const queryClient = useQueryClient();
    const patchResultsCache = usePatchOutreachResultsCache();

    // Unkeyed, unlike the row's own mutations: a batch belongs to no one row.
    const { addAsync } = useAddOutreachResultMutation(null, queryClient);
    const { archiveAsync } = useArchiveOutreachResultMutation(null, queryClient);
    const { unarchiveAsync } = useUnarchiveOutreachResultMutation(null, queryClient);

    // No single mutation's `isPending` covers a batch, and the buttons read which action is running.
    const [runningAction, setRunningAction] = useState<BulkAction | null>(null);

    const callFor: Record<BulkAction, (variables: { id: number }) => Promise<outreach.OutreachResult>> = {
        add: addAsync,
        archive: archiveAsync,
        unarchive: unarchiveAsync,
    };

    const runBulkAction = async (action: BulkAction, ids: number[]): Promise<BulkActionResult> => {
        if (!ids.length || runningAction) {
            return { succeededCount: 0, failedCount: 0, errorMessage: null };
        }

        setRunningAction(action);
        try {
            const settled = await Promise.allSettled(ids.map((id) => callFor[action]({ id })));
            const failed = settled.filter(isRejected);

            // Written once the batch has settled, so the rows arrive together rather than one at a time.
            patchResultsCache(settled.filter(isFulfilled).map((outcome) => outcome.value));

            return {
                succeededCount: settled.length - failed.length,
                failedCount: failed.length,
                errorMessage: failed.length ? errorUtils[action].getErrorMessage(failed[0].reason) : null,
            };
        } finally {
            setRunningAction(null);
        }
    };

    return { runBulkAction, runningAction };
}
